import type { IMFile } from '@wui/im'

import {
  IM_FILE_CUSTOM_PARAM_KEYS,
  type ChatFile,
  type ImFileMediaSize,
  type ImFileMediaSizeCustomParams,
  type ImFileMediaSizePayload,
} from '@/shared/types/files'

import { isImageFile, isVideoFile } from './type-helper'

const isPositiveFinite = (value: number): boolean => Number.isFinite(value) && value > 0

const isMediaSizePayload = (value: unknown): value is ImFileMediaSizePayload => {
  if (!value || typeof value !== 'object') {
    return false
  }
  const record = value as Record<string, unknown>
  return isPositiveFinite(Number(record.width)) && isPositiveFinite(Number(record.height))
}

export const serializeMediaSizePayload = (width: number, height: number): string | undefined => {
  if (!isPositiveFinite(width) || !isPositiveFinite(height)) {
    return undefined
  }
  const payload: ImFileMediaSizePayload = {
    width: Math.round(width),
    height: Math.round(height),
  }
  return JSON.stringify(payload)
}

export const deserializeMediaSizePayload = (raw?: string | null): ImFileMediaSize | undefined => {
  if (!raw) {
    return undefined
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isMediaSizePayload(parsed)) {
      return undefined
    }
    return {
      width: Math.round(Number(parsed.width)),
      height: Math.round(Number(parsed.height)),
    }
  } catch {
    return undefined
  }
}

export const buildMediaSizeCustomParams = (
  width: number,
  height: number,
): ImFileMediaSizeCustomParams | undefined => {
  const mediaSize = serializeMediaSizePayload(width, height)
  if (!mediaSize) {
    return undefined
  }
  return {
    [IM_FILE_CUSTOM_PARAM_KEYS.mediaSize]: mediaSize,
  }
}

export const parseMediaSizeCustomParams = (
  customParams?: Record<string, string> | null,
): ImFileMediaSize | undefined => {
  if (!customParams) {
    return undefined
  }
  return deserializeMediaSizePayload(customParams[IM_FILE_CUSTOM_PARAM_KEYS.mediaSize])
}

/**
 * Preview box for a single image/video: reserve height via aspect-ratio.
 * Width stays from CSS (`w-[248px]` / `w-full`) — do not set maxHeight here,
 * or tall media shrinks in width; parent `max-h-[476px] overflow-hidden` clips.
 */
export const getImFileMediaPreviewBoxStyle = (
  file: Pick<IMFile, 'customParams'>,
  fallbackRatio?: { width: number; height: number },
): Record<string, string> | undefined => {
  const size = parseMediaSizeCustomParams(file.customParams) ?? fallbackRatio
  if (!size) {
    return undefined
  }
  return {
    width: '100%',
    aspectRatio: `${size.width} / ${size.height}`,
  }
}

/** @deprecated use getImFileMediaPreviewBoxStyle */
export const getImFileMediaAspectStyle = (
  file: Pick<IMFile, 'customParams'>,
): { aspectRatio: string } | undefined => {
  const size = parseMediaSizeCustomParams(file.customParams)
  if (!size) {
    return undefined
  }
  return { aspectRatio: `${size.width} / ${size.height}` }
}

const readImageDimensions = (file: File): Promise<ImFileMediaSize | undefined> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(
        isPositiveFinite(image.naturalWidth) && isPositiveFinite(image.naturalHeight)
          ? { width: image.naturalWidth, height: image.naturalHeight }
          : undefined,
      )
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(undefined)
    }
    image.src = url
  })
}

const readVideoDimensions = (file: File): Promise<ImFileMediaSize | undefined> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve(
        isPositiveFinite(video.videoWidth) && isPositiveFinite(video.videoHeight)
          ? { width: video.videoWidth, height: video.videoHeight }
          : undefined,
      )
    }
    video.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(undefined)
    }
    video.src = url
  })
}

export const readMediaDimensions = async (file: File): Promise<ImFileMediaSize | undefined> => {
  const mime = file.type.toLowerCase()
  if (mime.startsWith('image/') || isImageFile(file.name)) {
    return readImageDimensions(file)
  }
  if (mime.startsWith('video/') || isVideoFile(file.name)) {
    return readVideoDimensions(file)
  }
  return undefined
}

/** Fills ChatFile.width/height for image/video when missing. */
export const enrichChatFilesWithMediaDimensions = async (files: ChatFile[]): Promise<void> => {
  await Promise.all(files.map(async (chatFile) => {
    if (chatFile.width && chatFile.height) {
      return
    }
    const size = await readMediaDimensions(chatFile.file)
    if (!size) {
      return
    }
    chatFile.width = size.width
    chatFile.height = size.height
  }))
}

export const chatFileToImFile = (chatFile: ChatFile): IMFile => {
  const imFile: IMFile = {
    filecookie: chatFile.name,
    filename: chatFile.file.name,
    filemime: chatFile.file.type,
    filesize: chatFile.file.size,
  }
  const customParams = chatFile.width != null && chatFile.height != null
    ? buildMediaSizeCustomParams(chatFile.width, chatFile.height)
    : undefined
  if (customParams) {
    imFile.customParams = customParams
  }
  return imFile
}
